import { useStaticQuery, graphql } from "gatsby";
import { TestimonialCard } from "./TestimonialCard";
import { SubscriberCount } from "./SubscriberCount";
import { COLORS } from "../../styles";
import { Box } from "@vinomofo/components";
import styled from "styled-components";
import { useCarousel } from "../../hooks/use-carousel";

export interface Testimonial {
  id: number;
  text: string;
  author: string;
  date: string;
  avatar: string;
  score: number;
}

interface GraphQLTestimonial {
  id: string;
  name: string;
  title: string;
  profileImage: {
    url: string;
  };
  joinedDate: string;
  description: string;
  score: number;
}

const SectionWrapper = styled.section`
  padding: 50px 0 0px;
  overflow: hidden;
  z-index: 110;
  @media ${({ theme }) => theme.mq.max.tablet} {
    padding: 50px 0 50px;
  }
  @media ${({ theme }) => theme.mq.max.mobile} {
    padding: 50px 0;
  }
`;

const Container = styled(Box)`
  max-width: 1440px;
  margin: 0 auto;
  padding: 40px 40px;
  overflow: hidden;
`;

const CarouselWrapper = styled(Box)`
  position: relative;
  margin: 0 auto;
  max-width: 1000px;
  overflow: visible;
  padding: 0;
  transition: opacity 0.3s ease;
  padding-bottom: 50px;
`;

const CarouselTrack = styled(Box)`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc(33.333% - 10px);
  gap: 25px;
  transition: transform 0.5s ease;
  margin: 0 calc(-33.33% + 100px);

  @media ${({ theme }) => theme.mq.max.tablet} {
    margin: 0 -66.33%;
    gap: 10px;
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    grid-auto-columns: calc(33.333% - 10px);
    margin: 0 calc(-66.666% + 5px);
    gap: 20px;
    padding: 0 10px;
  }
`;

const CarouselSlide = styled(Box)<{ isActive: boolean }>`
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
  transition: all 0.3s ease;
`;

const NavigationDots = styled(Box)`
  display: flex;
  justify-content: center;
  margin-top: 70px;
  gap: 12px;
`;

const NavigationDot = styled.button<{ isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${({ isActive }) =>
    isActive ? COLORS.liquidGold : "rgba(255, 255, 255, 0.3)"};
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${COLORS.liquidGold};
    transform: scale(1.2);
  }
`;

export const TestimonialCarousel = () => {
  const { vinodata } = useStaticQuery(graphql`
    query {
      vinodata {
        testimonials {
          id
          name
          title
          profileImage {
            url
          }
          joinedDate
          description
          score
        }
      }
    }
  `);

  const graphqlTestimonials: GraphQLTestimonial[] = vinodata.testimonials;

  const testimonials: Testimonial[] = graphqlTestimonials.map(
    (testimonial, index) => ({
      id: index + 1,
      text: testimonial?.description,
      author: testimonial?.name,
      date: testimonial?.joinedDate,
      avatar: testimonial?.profileImage?.url || "",
      score: testimonial?.score,
    })
  );

  const extendedTestimonials = [
    testimonials[testimonials.length - 1],
    ...testimonials,
    testimonials[0],
  ];

  const {
    currentIndex,
    setCurrentIndex,
    handleMouseEnter,
    handleMouseLeave,
  } = useCarousel(testimonials?.length);

  return (
    <SectionWrapper>
      <Container>
        <SubscriberCount />
        <CarouselWrapper
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <CarouselTrack
            style={{
              transform: `translateX(calc(-${currentIndex} * (33.333% + 10px)))`,
            }}
          >
            {extendedTestimonials.map(
              (testimonial: Testimonial, index: number) => (
                <CarouselSlide
                  key={testimonial.id + "-" + index}
                  isActive={index === currentIndex + 1}
                >
                  <TestimonialCard testimonial={testimonial} />
                </CarouselSlide>
              )
            )}
          </CarouselTrack>
          <NavigationDots>
            {testimonials.map((_: Testimonial, index: number) => (
              <NavigationDot
                key={index}
                isActive={index === currentIndex}
                onClick={() => setCurrentIndex(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </NavigationDots>
        </CarouselWrapper>
      </Container>
    </SectionWrapper>
  );
};
